/**
 * MenuBehaviour port (D-232). Same contract as brand/header/bsg-header.js.
 * Disclosure buttons only. Do not assign menu roles.
 */
export function bindMenus(root) {
  if (!root) return () => {};
  const MENU = "[data-menu]";
  const TRIGGER = ":scope > [data-menu-trigger]";
  const timeToken = (name, fallback) => {
    const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    const value = parseFloat(raw);
    if (!Number.isFinite(value)) return fallback;
    return raw.endsWith("ms") ? value : raw.endsWith("s") ? value * 1000 : fallback;
  };
  const openDelay = timeToken("--bsg-nav-menu-open-delay", 120);
  const closeDelay = timeToken("--bsg-nav-menu-close-delay", 250);

  let open = null;
  let pinned = false;
  let pending = null;
  let openTimer = 0;
  let closeTimer = 0;

  const menuOf = (target) => {
    const menu = target instanceof Element ? target.closest(MENU) : null;
    return menu && root.contains(menu) ? menu : null;
  };
  const triggerOf = (menu) => menu.querySelector(TRIGGER);
  const clearTimers = () => {
    window.clearTimeout(openTimer);
    window.clearTimeout(closeTimer);
    pending = null;
  };
  const panelOf = (menu) => menu.querySelector("[data-menu-panel]");
  const setOpen = (menu, value) => {
    menu.toggleAttribute("data-open", value);
    const trigger = triggerOf(menu);
    if (trigger) trigger.setAttribute("aria-expanded", String(value));
    const panel = panelOf(menu);
    if (panel) {
      if (value) panel.removeAttribute("inert");
      else panel.setAttribute("inert", "");
    }
  };
  const openMenu = (menu, pin) => {
    clearTimers();
    if (open && open !== menu) setOpen(open, false);
    open = menu;
    pinned = pin;
    setOpen(menu, true);
  };
  const closeMenu = (returnFocus) => {
    clearTimers();
    if (!open) return;
    const menu = open;
    open = null;
    pinned = false;
    setOpen(menu, false);
    if (returnFocus) triggerOf(menu)?.focus();
  };

  const onPointerOver = (event) => {
    if (event.pointerType !== "mouse") return;
    const menu = menuOf(event.target);
    if (!menu) return;
    if (menu === open) {
      window.clearTimeout(closeTimer);
      return;
    }
    if (open) {
      openMenu(menu, false);
      return;
    }
    if (pending === menu) return;
    window.clearTimeout(openTimer);
    pending = menu;
    openTimer = window.setTimeout(() => openMenu(menu, false), openDelay);
  };
  const onPointerOut = (event) => {
    if (event.pointerType !== "mouse") return;
    const menu = menuOf(event.target);
    if (!menu) return;
    const to = event.relatedTarget;
    if (to instanceof Node && menu.contains(to)) return;
    if (pending === menu) {
      window.clearTimeout(openTimer);
      pending = null;
    }
    if (menu === open && !pinned) {
      window.clearTimeout(closeTimer);
      closeTimer = window.setTimeout(() => closeMenu(), closeDelay);
    }
  };
  const onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const trigger = target.closest("[data-menu-trigger]");
    if (trigger && root.contains(trigger)) {
      const menu = trigger.parentElement?.closest(MENU) ?? null;
      if (!menu) return;
      if (menu === open) {
        if (pinned) closeMenu();
        else {
          clearTimers();
          pinned = true;
        }
      } else {
        openMenu(menu, true);
      }
      return;
    }
    if (open && target.closest("a") && open.contains(target)) closeMenu();
  };
  const onKeyDown = (event) => {
    if (event.key !== "Escape") return;
    const disclosure = root.querySelector(".bsg-hdr__disclosure[open]");
    if (disclosure) {
      disclosure.removeAttribute("open");
      disclosure.querySelector("summary")?.focus();
      event.stopPropagation();
      return;
    }
    if (!open) return;
    event.stopPropagation();
    closeMenu(true);
  };
  const onFocusOut = (event) => {
    if (!open) return;
    const to = event.relatedTarget;
    if (to instanceof Node && !open.contains(to)) closeMenu();
  };
  const onDocumentPointerDown = (event) => {
    if (open && !(event.target instanceof Node && open.contains(event.target))) closeMenu();
  };

  root.addEventListener("pointerover", onPointerOver);
  root.addEventListener("pointerout", onPointerOut);
  root.addEventListener("click", onClick);
  root.addEventListener("focusout", onFocusOut);
  document.addEventListener("keydown", onKeyDown);
  document.addEventListener("pointerdown", onDocumentPointerDown);
  root.setAttribute("data-menus", "");
  root.querySelectorAll("[data-menu-panel]").forEach((panel) => {
    if (!panel.parentElement?.hasAttribute("data-open")) panel.setAttribute("inert", "");
  });
  return () => {
    closeMenu();
    root.removeEventListener("pointerover", onPointerOver);
    root.removeEventListener("pointerout", onPointerOut);
    root.removeEventListener("click", onClick);
    root.removeEventListener("focusout", onFocusOut);
    document.removeEventListener("keydown", onKeyDown);
    document.removeEventListener("pointerdown", onDocumentPointerDown);
    root.removeAttribute("data-menus");
    root.querySelectorAll("[data-menu-panel][inert]").forEach((panel) => panel.removeAttribute("inert"));
  };
}